import { useEffect, useRef, useState } from 'react';
import { useOptionalPodcastPlayer } from '@/contexts/PodcastPlayerContext';
import { PodcastPanel } from './PodcastPanel';
import { PodcastPill } from './PodcastPill';

/** The lesson URL that opens a section's podcast on the Podcast tab. */
export function podcastHref(lessonId: string, sectionIndex: number): string {
  return `/my-learning/${lessonId}?tab=lesson&view=audio&episode=${sectionIndex}`;
}

/**
 * The Navbar's podcast: the pill plus the panel it opens. Renders nothing
 * while no podcast is loaded, while its lesson's Text reader is open, or
 * outside a `PodcastPlayerProvider`.
 *
 * Click-outside and Escape as `FocusPillMenu` does them.
 */
export function PodcastPillMenu({ className }: { className?: string }) {
  const player = useOptionalPodcastPlayer();
  const [open, setOpen] = useState(false);
  const pillRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (pillRef.current?.contains(target)) return;
      if (!panelRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      pillRef.current?.focus();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const track = player?.track;
  // On this lesson's Text reader the podcast shows in the text itself
  // (`SectionAudioStrip` / `PodcastDock`), so the pill steps aside there.
  if (!player || !track || player.readingLesson === track.lessonId) {
    return null;
  }

  return (
    <div className={className}>
      <div className='relative'>
        <PodcastPill
          ref={pillRef}
          isPlaying={player.isPlaying}
          sectionIndex={track.sectionIndex}
          sectionTitle={track.sectionTitle}
          currentTime={player.currentTime}
          duration={player.duration}
          expanded={open}
          onToggle={player.toggle}
          onOpen={() => setOpen(o => !o)}
        />
        {open && (
          <PodcastPanel
            ref={panelRef}
            className='absolute right-0 top-11 z-50'
            lessonTitle={track.lessonTitle}
            peaks={track.peaks}
            sectionIndex={track.sectionIndex}
            sectionTitle={track.sectionTitle}
            isPlaying={player.isPlaying}
            currentTime={player.currentTime}
            duration={player.duration}
            transcriptHref={podcastHref(track.lessonId, track.sectionIndex)}
            onToggle={player.toggle}
            onSeek={player.seek}
            onClose={() => {
              setOpen(false);
              player.close();
            }}
            onNavigate={() => setOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
